import '../../../styles/components/navbar.css';

import { Menu, Person, Search } from '@mui/icons-material';
import ShoppingBagIcon from '@mui/icons-material/ShoppingBag';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import CloseIcon from '@mui/icons-material/Close';
import LoginIcon from '@mui/icons-material/Login';
import { Badge, IconButton, Drawer } from '@mui/material';
import React, { useEffect, useState, useRef } from 'react';
import { Link} from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';

import { useAuth } from '../../../context/AuthContext';
import { useCategory } from '../../../context/CategoryContext';
import { useShoppingList } from '../../../context/ShoppingListContext';
import { useConfig } from '../../../utils/useConfig'; // Dodaj import hooka konfiguracji
import { Category } from '../../../models/Category';
import { UserRole } from '../../../models/User';
import SearchBar from '../../SearchBar';
import ShoppingList from '../../shoppingPanel/ShoppingPanel';
import NavBarCategories from './NavBarCategories';

// Nowy komponent dla paska promocyjnego
const PromoStripe = ({ backgroundColor, textColor }: { backgroundColor: string, textColor: string }) => {
  const variants = {
    animate: {
      x: [20, 0, 0],
      opacity: [0, 1, 1],
      transition: {
        duration: 2,
        repeat: Infinity,
        repeatType: 'loop' as const,
        repeatDelay: 5,
      }
    }
  };

  return (
    <div 
      className="w-full py-2 text-center text-sm font-medium"
      style={{ backgroundColor }}
    >
      <motion.div
        className="container mx-auto flex items-center justify-center space-x-1 px-4"
        initial="initial"
        animate="animate"
        variants={variants}
      >
        <span className="hidden sm:inline" style={{ color: textColor }}>
          Zarejestruj się i zapisz do newslettera, a otrzymasz
        </span>
        <span className="sm:hidden" style={{ color: textColor }}>
          Zarejestruj się i odbierz
        </span>
        <span 
          className="font-bold text-base"
          style={{ color: textColor }}
        >
          10% rabatu
        </span>
        <span style={{ color: textColor }}>na pierwsze zamówienie!</span>
        <Link 
          to="/regulamin-promocji" 
          className="underline hover:no-underline font-normal ml-1 whitespace-nowrap"
          style={{ color: textColor }}
        >
          Regulamin promocji
        </Link>
      </motion.div>
    </div>
  );
}

function NavBar() {
  // Pobierz konfigurację
  const config = useConfig();
  const { company, theme, ui } = config;

  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [scrolled, setScrolled] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const navRef = useRef<HTMLDivElement>(null);

  // Kontekst i stan
  const { getCategories } = useCategory();
  const [categories, setCategories] = useState<Category[]>([]);
  const { user } = useAuth();
  const {
    shoppingList,
    isShoppingListOpen,
    setForeshadow,
    setIsShoppingListOpen,
  } = useShoppingList();

  const [isShaking, setIsShaking] = useState(false);
  const cartItemCount = shoppingList.reduce(
    (acc, item) => acc + item.quantity,
    0,
  );

  // Nowa funkcja do generowania inicjałów użytkownika
  const getUserInitials = () => {
    if (!user) return '';
    return `${user.name.charAt(0)}${user.surname.charAt(0)}`.toUpperCase();
  };

  // Funkcja do generowania koloru awatara bazująca na id użytkownika
  const getAvatarColor = () => {
    if (!user) return 'bg-blue-500';
    const colors = [
      'bg-blue-500',
      'bg-green-500',
      'bg-purple-500',
      'bg-amber-500',
      'bg-pink-500',
      'bg-indigo-500',
    ];

    const index = user.id.charCodeAt(0) % colors.length;
    return colors[index];
  };

  // Stan do śledzenia błędów ładowania obrazka
  const [profileImageError, setProfileImageError] = useState(false);

  // Obsługa zmiany szerokości okna
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
      if (window.innerWidth > 1024) {
        setShowSearch(false);
        setMobileMenuOpen(false);
      }
    };

    const handleScroll = () => {
      const offset = window.scrollY;
      setScrolled(offset > 20);
    };

    window.addEventListener('resize', handleResize);
    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  // Efekt animacji koszyka
  useEffect(() => {
    if (shoppingList.length > 0) {
      setIsShaking(true);
      setTimeout(() => setIsShaking(false), 300);
    }
  }, [shoppingList]);

  // Pobieranie kategorii
  useEffect(() => {
    async function fetchCategories() {
      try {
        const response = await getCategories();
        setCategories(response.categories);
      } catch (e) {
        console.error(e);
      }
    }

    fetchCategories();
  }, [getCategories]);

  // Kliknięcie poza menu zamyka je
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        mobileMenuOpen &&
        navRef.current &&
        !navRef.current.contains(event.target as Node)
      ) {
        setMobileMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [mobileMenuOpen]);

  const isMobile = windowWidth <= 1024;
  // Użyj konfiguracji do określenia czy pokazać kategorie na stronie głównej
  const showCategoriesOnHomepage = ui.navbar!.showCategoriesOnHomepage;

  return (
    <>
      {/* <PromoStripe 
        backgroundColor={theme.colors.secondary || '#4338CA'} 
        textColor={'white'} 
      /> */}
      <motion.div
        className={`sticky w-full top-0 z-40 ${scrolled ? 'shadow-md' : ''}`}
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.3 }}
      >
        <div
          className={`w-full bg-white transition-all duration-200 ${
            scrolled ? 'py-2' : 'py-4'
          } bg-opacity-95 backdrop-blur-md`}
          ref={navRef}
        >
          <div className="mx-auto px-4">
            <div className="flex items-center justify-between">
              {/* Logo */}
              <div className="flex items-center">
                {isMobile && (
                  <IconButton
                    onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                    className="mr-2"
                    aria-label="menu"
                  >
                    <Menu />
                  </IconButton>
                )}

                <Link to="/" className="flex items-center space-x-2 group">
                  <motion.img
                    src={company.logo}
                    alt={`${company.name} Logo`}
                    className={`${isMobile ? 'h-8 w-8' : 'h-10 w-10'}`}
                    whileHover={{ rotate: 10, scale: 1.05 }}
                    transition={{ duration: 0.2 }}
                  />
                  <h1
                    className={`font-display uppercase font-bold tracking-wider text-gray-800 group-hover:text-${theme.colors.primary} transition-colors ${
                      isMobile ? 'text-lg' : 'text-2xl'
                    }`}
                    style={{
                      color: theme.colors.text,
                    }}
                  >
                    {company.name}
                  </h1>
                </Link>
              </div>

              {/* Desktop Search Bar */}
              {!isMobile && !showSearch && (
                <div className="flex-grow max-w-2xl mx-8">
                  <SearchBar />
                </div>
              )}

              {/* Menu ikony */}
              <div className="flex items-center space-x-2">
                {isMobile && (
                  <IconButton
                    onClick={() => setShowSearch(!showSearch)}
                    aria-label="search"
                    style={{ color: theme.colors.primary }}
                  >
                    {showSearch ? <CloseIcon /> : <Search />}
                  </IconButton>
                )}

                {user?.role === UserRole.Admin && (
                  <IconButton
                    component={Link}
                    to="/admin"
                    aria-label="admin panel"
                    className="text-purple-700"
                  >
                    <AdminPanelSettingsIcon
                      fontSize={isMobile ? 'medium' : 'large'}
                      sx={{ color: theme.colors.primary }}
                    />
                  </IconButton>
                )}

                {/* Zastąpienie ikony użytkownika */}
                <IconButton
                  component={Link}
                  to={user ? '/profil' : '/logowanie'}
                  aria-label={user ? 'profil' : 'zaloguj się'}
                  style={{ color: theme.colors.primary }}
                >
                  {!user ? (
                    <LoginIcon
                      fontSize={isMobile ? 'medium' : 'large'}
                      sx={{ color: theme.colors.primary }}
                    />
                  ) : user.profilePicture && !profileImageError ? (
                    <div className="relative">
                      <img
                        src={user.profilePicture}
                        alt={`${user.name} ${user.surname}`}
                        className={`rounded-full object-cover border border-gray-200 ${isMobile ? 'w-6 h-6' : 'w-8 h-8'}`}
                        onError={() => setProfileImageError(true)}
                      />
                    </div>
                  ) : (
                    <div
                      className={`${isMobile ? 'w-6 h-6' : 'w-8 h-8'} rounded-full ${getAvatarColor()} text-white flex items-center justify-center font-semibold text-sm`}
                    >
                      {getUserInitials()}
                    </div>
                  )}
                </IconButton>

                <IconButton
                  onClick={() => setIsShoppingListOpen(!isShoppingListOpen)}
                  onMouseEnter={() => setForeshadow(true)}
                  onMouseLeave={() => setForeshadow(false)}
                  className={`transition-all ${isShaking ? 'text-green-600' : ''}`}
                  aria-label="shopping cart"
                  sx={{
                    padding: '8px',
                    color: isShaking
                      ? theme.colors.secondary
                      : theme.colors.primary,
                  }}
                >
                  <div className="flex items-center justify-center">
                    <Badge
                      badgeContent={cartItemCount}
                      color="primary"
                      max={99}
                      sx={{
                        '& .MuiBadge-badge': {
                          right: -3,
                          top: 5,
                          backgroundColor: theme.colors.primary,
                        },
                      }}
                    >
                      <ShoppingBagIcon
                        fontSize={isMobile ? 'medium' : 'large'}
                      />
                    </Badge>
                  </div>
                </IconButton>
              </div>
            </div>

            {/* Mobile Search Bar - pokazuje się po kliknięciu ikony wyszukiwania */}
            <AnimatePresence>
              {isMobile && showSearch && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  className="overflow-hidden mt-3"
                >
                  <SearchBar />
                </motion.div>
              )}
            </AnimatePresence>

            {/* Desktop Categories - używa konfiguracji do określenia czy pokazać na stronie głównej */}
            {!isMobile && showCategoriesOnHomepage && (
              <div className="mt-4">
                <NavBarCategories categories={categories} />
              </div>
            )}
          </div>
        </div>
      </motion.div>

      {/* Mobile Menu */}
      <Drawer
        anchor="left"
        open={mobileMenuOpen}
        onClose={() => setMobileMenuOpen(false)}
        PaperProps={{
          sx: {
            width: '75%',
            maxWidth: 300,
            pt: 2,
            bgcolor: 'white',
          },
        }}
      >
        <div className="p-4 h-full flex flex-col">
          <div className="flex items-center justify-between mb-6 pb-4 border-b">
            <h2
              className="font-display text-xl font-semibold"
              style={{ color: theme.colors.text, fontFamily: theme.fontFamily }}
            >
              Menu
            </h2>
            <IconButton onClick={() => setMobileMenuOpen(false)}>
              <CloseIcon />
            </IconButton>
          </div>

          <nav className="flex-grow">
            <ul className="space-y-3">
              {/* Opcje menu z konfiguracji */}
              {ui.navbar!.mobileMenuOptions!.map((option, index) => (
                <li key={index}>
                  <Link
                    to={option.path}
                    className="flex items-center py-2 px-3 rounded-md hover:bg-gray-50 text-gray-700 hover:text-blue-600 transition-colors"
                    onClick={() => setMobileMenuOpen(false)}
                    style={{
                      color: theme.colors.text,
                      fontFamily: theme.fontFamily,
                    }}
                  >
                    {option.label}
                  </Link>
                </li>
              ))}
              {categories.map((category) => (
                <li key={category.id}>
                  <Link
                    to={`/szukaj?categoryId=${category.id}`}
                    className={`flex items-center py-2 px-3 rounded-md hover:bg-gray-50 transition-colors hover:${theme.colors.primary}`}
                    onClick={() => setMobileMenuOpen(false)}
                    style={{
                      color: theme.colors.text,
                      fontFamily: theme.fontFamily,
                    }}
                  >
                    {category.name}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>

          {user ? (
            <div className="pt-4 border-t">
              {ui.navbar!.userMenuOptions!.map((option, index) => (
                <Link
                  key={index}
                  to={option.path}
                  className="flex items-center justify-center py-3 px-4 rounded-md bg-gray-100 hover:bg-gray-200 transition-colors mb-2"
                  onClick={() => setMobileMenuOpen(false)}
                  style={{
                    color: theme.colors.text,
                    fontFamily: theme.fontFamily,
                  }}
                >
                  {index === 0 && <Person className="mr-2" />}
                  {option.label}
                </Link>
              ))}
            </div>
          ) : (
            <div className="pt-4 border-t flex flex-col space-y-2">
              <Link
                to="/logowanie"
                className="flex items-center justify-center py-3 px-4 rounded-md text-white transition-colors"
                onClick={() => setMobileMenuOpen(false)}
                style={{
                  backgroundColor: theme.colors.primary,
                  fontFamily: theme.fontFamily,
                }}
              >
                Zaloguj się
              </Link>
              <Link
                to="/rejestracja"
                className="flex items-center justify-center py-3 px-4 rounded-md border border-gray-300 hover:bg-gray-50 transition-colors"
                onClick={() => setMobileMenuOpen(false)}
                style={{
                  color: theme.colors.text,
                  fontFamily: theme.fontFamily,
                }}
              >
                Zarejestruj się
              </Link>
            </div>
          )}
        </div>
      </Drawer>

      {/* Shopping Cart */}
      <ShoppingList
        isShoppingListOpen={isShoppingListOpen}
        setIsShoppingListOpen={setIsShoppingListOpen}
      />
    </>
  );
}

export default NavBar;
